<template>
  <v-chip
    v-if="chip"
    v-bind="$attrs"
    :variant="mode === 'outlined' || color === 'beta' ? 'outlined' : undefined"
    :class="[type, 'wasabi-pill', `${id}`, getTheme, mode, { small: size == 'small', 'beta-pill': color === 'beta' }]"
    :closable="type === 'interactive'"
    :data-testid="sectionTag"
    :style="[{ maxWidth: maxWidth }, pillColorStyle]"
  >
    <v-icon
      v-if="icon && type === 'static' && iconPosition === 'start'"
      :data-testid="sectionTag ? `${sectionTag}-icon` : undefined"
      :icon="icon"
      class="pill_icon"
    ></v-icon>
    <slot name="label">
      <span
        :class="getLabelClass"
        :data-testid="sectionTag ? `${sectionTag}-label` : undefined"
        class="text-truncate"
      >
        {{ label }}
      </span>
    </slot>
    <slot name="suffix"></slot>
    <div
      v-if="type === 'interactive'"
      class="interactive-hit-state-close"
      :data-testid="sectionTag ? `${sectionTag}-close` : undefined"
      @click="closeChip"
    ></div>
    <v-icon
      v-if="icon && type === 'static' && iconPosition === 'end'"
      :data-testid="sectionTag ? `${sectionTag}-icon` : undefined"
      :icon="icon"
      class="pill_icon"
    ></v-icon>
  </v-chip>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import colors from '@/plugins/vuetify/colors.mjs'
import { tokens } from '@/plugins/vuetify'

export type PillType = 'static' | 'interactive'
export type PillColor = 'green' | 'blue' | 'red' | 'gray' | 'yellow' | 'beta'
export type PillMode = 'light' | 'dark' | 'outlined'
export type PillSize = 'large' | 'small' | 'xl'
export type PillIconPosition = 'start' | 'end'

export interface WPillsProps {
  sectionTag?: string
  id?: string
  type?: PillType
  label?: string
  pointerEvents?: string
  color?: PillColor
  mode?: PillMode
  icon?: string
  labelClass?: string
  size?: PillSize
  iconPosition?: PillIconPosition
  maxWidth?: string
}

const props = withDefaults(defineProps<WPillsProps>(), {
  sectionTag: 'unspecified',
  id: '',
  type: 'static',
  label: '',
  pointerEvents: '',
  color: 'gray',
  mode: 'light',
  icon: '',
  labelClass: '',
  size: 'large',
  iconPosition: 'end',
  maxWidth: '100%'
})

const emit = defineEmits(['close'])

const closeChip = () => {
  emit('close')
}

const chip = ref(true)

const theme = computed(() => ({
  staticBcgColor: tokens.color.status.neutral.surface_alt,
  staticTextColor: tokens.color.status.neutral.text,
  interactiveBcgColor: tokens.color.action.primary.surface,
  interactiveTextColor: tokens.color.action.primary.text,
  interactiveIconColor: tokens.color.content.icons.inverse,
}))

const betaBorderColor = colors.yellow.yellow600
const betaGradientStart = colors.yellow.yellow600
const betaGradientEnd = colors.orange.orange600

const modeColorClasses: Record<string, Record<string, string>> = {
  dark: {
    green: 'bg-green600 text-white',
    red: 'bg-red600 text-white',
    gray: 'bg-neutral700 text-white',
  },
  outlined: {
    gray: 'bg-white text-neutral700',
  },
}

const pillColorStyle = computed((): Record<string, string> => {
  if (props.color === 'beta') return {}

  if (props.type === 'static' && props.mode === 'light') {
    const colorMap: Record<string, { bg: string; text: string }> = {
      green: { bg: tokens.color.status.success.surface, text: tokens.color.status.success.text },
      blue: { bg: tokens.color.status.info.surface, text: tokens.color.status.info.text },
      red: { bg: tokens.color.status.danger.surface, text: tokens.color.status.danger.text },
      gray: { bg: tokens.color.status.neutral.surface_alt, text: tokens.color.status.neutral.text },
      yellow: { bg: tokens.color.status.warning.surface, text: tokens.color.status.warning.text },
    }
    const c = colorMap[props.color]
    if (c) return { backgroundColor: c.bg, color: c.text }
  }

  if (props.type === 'interactive') {
    return {
      backgroundColor: tokens.color.action.primary.surface,
      color: tokens.color.action.primary.text,
    }
  }

  return {}
})

const getTheme = computed(() => {
  if (props.mode === 'light' || props.type === 'interactive') return ''
  return modeColorClasses[props.mode]?.[props.color] || ''
})

const calcPointerEvents = computed((): string => {
  if (props.pointerEvents) return props.pointerEvents
  return 'all'
})

const getLabelClass = computed(() => {
  if (props.color === 'beta') {
    return `label-semibold-extrasmall beta-gradient-text ${props.labelClass}`
  }
  if (props.type == 'static') {
    if (props.size == 'small') {
      return `label-semibold-extrasmall ${props.labelClass}`
    } else if (props.size == 'xl') {
      return `label-semibold-medium ${props.labelClass}`
    } else {
      return `label-semibold-small ${props.labelClass}`
    }
  }
  if (props.type == 'interactive') {
    return `label-semibold-small ${props.labelClass}`
  }
  return props.labelClass
})
</script>

<style lang="scss" scoped>
* {
  box-sizing: border-box;
}

.wasabi-pill {
  padding: 0 16px !important;

  .interactive-hit-state-close {
    z-index: 2;
    width: 34px;
    height: 28px;
    position: absolute;
    right: 0;
    cursor: pointer;
  }

  &.static {
    justify-content: center;
    height: 26px;
    padding: 4px 16px !important;
    background-color: v-bind('theme.staticBcgColor');
    color: v-bind('theme.staticTextColor');

    &.small {
      height: auto;
      padding: 0 10px !important;
      justify-content: flex-start;
    }
  }

  &.interactive {
    background-color: v-bind('theme.interactiveBcgColor');
    color: v-bind('theme.interactiveTextColor');
    height: 26px;
    padding: 4px 16px !important;
  }

  &.dark {
    height: 18px;
    padding: 0 16px !important;
  }

  &.outlined {
    height: 18px;
    padding: 0 16px !important;
  }

  &.beta-pill {
    height: 17px !important;
    padding: 0 8px !important;
    min-height: unset;
    background-color: transparent !important;
    border: 1px solid v-bind('betaBorderColor') !important;
  }

  .beta-gradient-text {
    background: linear-gradient(90deg, v-bind('betaGradientStart'), v-bind('betaGradientEnd'));
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    text-transform: uppercase;
    user-select: none;
  }

  .pill_icon {
    width: 14px;
    height: 14px;
  }

  :deep(.v-chip__content) {
    pointer-events: v-bind('calcPointerEvents');
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  :deep(.v-chip__underlay) {
    background: none !important;
  }

  :deep(.v-chip__content .v-icon) {
    margin-left: v-bind('props.iconPosition === "end" ? "6px" : "0"') !important;
    margin-right: v-bind('props.iconPosition === "start" ? "6px" : "0"') !important;
  }

  :deep(.v-icon--size-default) {
    font-size: unset !important;
  }
}

.wasabi-pill :deep(.v-chip__close) {
  margin-inline-start: 6px;
  margin-inline-end: unset;
  font-size: 14px;
  color: v-bind('theme.interactiveIconColor') !important;
}
</style>
